import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import BRAND from '../../config/branding';
import EnhancedSignatureCapture from '../../components/documents/EnhancedSignatureCapture';
import { filterClientFacingSections, normalizeProposalPreviewSections, parseStructuredContent } from '../../../../shared/contractPreview';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const fmtCurrency = (v) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
const HEADER_LOGO = '/branding/stratel-logo-light.png';

const getProposalTotals = (doc) => {
  const lineItems = doc?.lineItems || [];
  const subtotal = Number(doc?.subtotal ?? lineItems.reduce((sum, item) => sum + Number(item.amount || 0), 0));
  const discountAmount = Number(doc?.discount?.amount ?? 0);
  const taxAmount = Number(doc?.tax?.amount ?? 0);
  const total = Number(doc?.total ?? (subtotal - discountAmount + taxAmount));
  return { subtotal, discountAmount, taxAmount, total };
};

const getSelectedProposalTotals = (doc, lineItems, selectedIds) => {
  const selectedItems = lineItems.filter((item) =>
    selectedIds.includes(String(item._id || item.id || ''))
  );
  const subtotal = selectedItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const originalSubtotal = lineItems.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const percent = Math.max(0, Number(doc?.discount?.percent || 0));
  const fixedDiscount = Math.max(0, Number(doc?.discount?.value || doc?.discount?.amount || 0));
  const discountAmount = Math.min(
    subtotal,
    percent > 0
      ? subtotal * percent / 100
      : originalSubtotal > 0 ? fixedDiscount * subtotal / originalSubtotal : 0
  );
  const taxRate = Math.max(0, Number(doc?.tax?.rate || 0));
  const taxAmount = (subtotal - discountAmount) * taxRate / 100;
  return {
    subtotal,
    discountAmount,
    taxAmount,
    total: subtotal - discountAmount + taxAmount,
  };
};

const formatLegalAddress = (address = {}) => {
  const lines = [
    address?.line1,
    address?.line2,
    [address?.city, address?.state, address?.postalCode].filter(Boolean).join(', '),
    address?.country && !['US', 'USA'].includes(String(address.country).toUpperCase()) ? address.country : null,
  ].filter(Boolean);
  return lines.join('\n');
};

const DOC_THEME = {
  proposal: { from: '#e7faf7', via: '#9ce7dd', to: '#0f766e', text: '#0f172a', subtext: '#0f766e', cta: 'bg-teal-700 hover:bg-teal-600' },
  nda: { from: '#ecfeff', via: '#a5f3fc', to: '#0f766e', text: '#0f172a', subtext: '#0f766e', cta: 'bg-teal-700 hover:bg-teal-600' },
  consultant_agreement: { from: '#eef2ff', via: '#c7d2fe', to: '#4338ca', text: '#0f172a', subtext: '#4338ca', cta: 'bg-indigo-700 hover:bg-indigo-600' },
  msa: { from: '#e6fffb', via: '#99f6e4', to: '#0d9488', text: '#0f172a', subtext: '#0d9488', cta: 'bg-teal-700 hover:bg-teal-600' },
  sow: { from: '#f0fdfa', via: '#99f6e4', to: '#0f766e', text: '#0f172a', subtext: '#0f766e', cta: 'bg-teal-700 hover:bg-teal-600' },
  ccda: { from: '#ccfbf1', via: '#5eead4', to: '#0d9488', text: '#0f172a', subtext: '#0d9488', cta: 'bg-teal-700 hover:bg-teal-600' },
};

const getDocTheme = (type) => DOC_THEME[type] || DOC_THEME.proposal;

function renderSectionBlocks(section, docType) {
  const blocks = parseStructuredContent(section.content, section.key, { docType });
  return blocks.map((block, index) => {
    if (block.type === 'table') {
      return (
        <div key={`${section.key}-table-${index}`} className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {block.headers.map((header) => (
                  <th key={header} className="px-3 py-2 text-left font-semibold border-b border-gray-200">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={`${section.key}-row-${rowIndex}`} className="border-b border-gray-100 last:border-b-0">
                  {block.headers.map((header, cellIndex) => (
                    <td key={`${header}-${rowIndex}`} className="px-3 py-2 align-top text-gray-700 whitespace-pre-wrap">
                      {row[cellIndex] || '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return (
      <div
        key={`${section.key}-text-${index}`}
        className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed"
      >
        {block.content}
      </div>
    );
  });
}

function StatusBanner({ status, typeLabel = 'document', requiresSignatureCapture = false }) {
  if (status === 'accepted') return (
    <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-5 py-4 mb-8">
      <CheckCircle size={20} />
      <span className="font-semibold">
        You have {requiresSignatureCapture ? 'signed' : 'accepted'} this {typeLabel.toLowerCase()}.
      </span>
    </div>
  );
  if (status === 'declined') return (
    <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl px-5 py-4 mb-8">
      <XCircle size={20} />
      <span className="font-semibold">You have declined this {typeLabel.toLowerCase()}.</span>
    </div>
  );
  if (status === 'expired') return (
    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-5 py-4 mb-8">
      <Clock size={20} />
      <span className="font-semibold">This {typeLabel.toLowerCase()} has expired. Please contact us for an updated version.</span>
    </div>
  );
  return null;
}

export default function ProposalPublicPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [issuerProfile, setIssuerProfile] = useState({
    issuerName: '',
    issuerEmail: '',
    legalAddress: {},
    legalAddressFormatted: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [responding, setResponding] = useState(false);
  const [responseResult, setResponseResult] = useState(null);
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showAcceptForm, setShowAcceptForm] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerAgreed, setSignerAgreed] = useState(false);
  const [selectedLineItemIds, setSelectedLineItemIds] = useState([]);
  const [openSections, setOpenSections] = useState({});
  const previewQuery = searchParams.get('preview') === '1' ? '?preview=1' : '';

  useEffect(() => {
    fetch(`${API_BASE}/proposals/public/${token}${previewQuery}`)
      .then(async (r) => {
        if (r.status === 404 || r.status === 410) {
          const d = await r.json().catch(() => ({}));
          throw new Error(d.message || 'This link is expired or not found.');
        }
        if (!r.ok) throw new Error('Failed to load document.');
        return r.json();
      })
      .then((d) => {
        const proposal = d?.proposal || d;
        setData(proposal);
        setSelectedLineItemIds(
          (proposal?.lineItems || []).map((item) => String(item._id || item.id || '')).filter(Boolean)
        );
        setSignerName(proposal?.recipientName || '');
        setSignerEmail(proposal?.recipientEmail || '');
        setIssuerProfile({
          issuerName: d?.issuerProfile?.issuerName || '',
          issuerEmail: d?.issuerProfile?.issuerEmail || '',
          legalAddress: d?.issuerProfile?.legalAddress || {},
          legalAddressFormatted:
            d?.issuerProfile?.legalAddressFormatted || formatLegalAddress(d?.issuerProfile?.legalAddress || {}),
        });
        // Open first section by default
        if (proposal?.sections?.length) {
          setOpenSections({ [proposal.sections[0].key]: true });
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, previewQuery]);

  const respond = async (action, options = {}) => {
    if (action === 'accept' && !showAcceptForm) {
      setShowAcceptForm(true);
      return;
    }
    if (action === 'decline' && !showDeclineForm) {
      setShowDeclineForm(true);
      return;
    }
    setResponding(true);
    setError('');
    try {
      const r = await fetch(`${API_BASE}/proposals/public/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          declineReason: declineReason.trim(),
          signerName: options.signerName ?? signerName.trim(),
          signerEmail: options.signerEmail ?? signerEmail.trim(),
          signatureData: options.signatureData,
          metadata: options.metadata,
          selectedLineItemIds: action === 'accept' && doc.type === 'proposal'
            ? selectedLineItemIds
            : undefined,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Response failed.');
      setResponseResult(action);
      setData((prev) => ({
        ...prev,
        status: action === 'accept' ? 'accepted' : 'declined',
        acceptanceSelection: d.acceptanceSelection || prev.acceptanceSelection,
      }));
      setShowDeclineForm(false);
      setShowAcceptForm(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setResponding(false);
    }
  };

  const toggleSection = (key) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-700" />
    </div>
  );

  if (error && !data) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
        <AlertTriangle size={48} className="text-amber-500 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Link Unavailable</h2>
        <p className="text-gray-500">{error}</p>
        <p className="text-sm text-gray-400 mt-4">
          Please contact <strong>{issuerProfile.issuerName || BRAND.name}</strong> to request a new link.
        </p>
      </div>
    </div>
  );

  const doc = data;
  const requiresSignatureCapture = ['nda', 'msa', 'sow', 'ccda', 'consultant_agreement'].includes(doc.type);
  const isMsa = doc.type === 'msa';
  const typeLabel = doc.type === 'msa' ? 'Master Service Agreement'
    : doc.type === 'sow'  ? 'Statement of Work'
    : doc.type === 'ccda' ? 'Custom Course Development Agreement'
    : doc.type === 'nda' ? 'Non-Disclosure Agreement'
    : doc.type === 'consultant_agreement' ? 'External Consultant Agreement'
    : 'Proposal';
  const docTheme = getDocTheme(doc.type);
  const isTerminal = ['accepted', 'declined', 'expired', 'cancelled'].includes(doc.status);
  const lineItems = doc.lineItems || [];
  const isSelectableProposal = doc.type === 'proposal' && lineItems.length > 0;
  const totals = doc.acceptanceSelection
    ? {
        subtotal: Number(doc.acceptanceSelection.subtotal || 0),
        discountAmount: Number(doc.acceptanceSelection.discount?.amount || 0),
        taxAmount: Number(doc.acceptanceSelection.tax?.amount || 0),
        total: Number(doc.acceptanceSelection.total || 0),
      }
    : isSelectableProposal && !isTerminal
      ? getSelectedProposalTotals(doc, lineItems, selectedLineItemIds)
      : getProposalTotals(doc);
  const previewSections = doc.type === 'proposal'
    ? normalizeProposalPreviewSections(doc.sections || [])
    : filterClientFacingSections(doc.sections || []);
  const lmsCommercial = doc.lmsCommercialSnapshot || null;
  const soldPackageTitles = Array.isArray(lmsCommercial?.selectedPackageSummaries)
    ? lmsCommercial.selectedPackageSummaries.map((item) => item.title).filter(Boolean)
    : [];
  const soldCourseCount = Array.isArray(lmsCommercial?.selectedCourseIds)
    ? lmsCommercial.selectedCourseIds.length
    : 0;
  const hasInvestmentSection = previewSections.some((section) => section.key === 'investment');
  const showsCommercialScope = ['proposal', 'msa', 'sow', 'ccda'].includes(doc.type);
  const commercialScopeBanner = doc.facilityName
    ? `This document reflects the approved commercial package for ${doc.facilityName} only. Any additional facilities require a separate expansion or revised commercial package.`
    : `This document reflects the organization-wide commercial package for ${doc.recipientCompany || 'the client organization'}. Any facilities outside this approved package require a separate expansion or revised commercial package.`;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Letterhead */}
        <div className="relative rounded-t-2xl overflow-hidden text-white">
          <div
            className="absolute inset-0"
            style={{
              background: `linear-gradient(90deg, ${docTheme.from} 0%, ${docTheme.via} 40%, ${docTheme.to} 100%)`,
            }}
          />
          <div className="relative px-8 py-5 flex items-center justify-between">
            <div className="flex items-center gap-5">
              <img
                src={BRAND.logo}
                alt={`${BRAND.name} logo`}
                className="h-32 w-auto object-contain drop-shadow-[0_1px_4px_rgba(0,0,0,0.35)] flex-shrink-0"
                onError={(e) => { e.currentTarget.src = HEADER_LOGO; }}
              />
              <div>
                <h1 className="text-3xl font-bold tracking-tight" style={{ color: docTheme.text }}>{issuerProfile.issuerName || BRAND.name}</h1>
                <p className="text-xs font-semibold uppercase tracking-wide mt-1" style={{ color: docTheme.subtext }}>{BRAND.tagline}</p>
                <p className="text-sm font-semibold mt-2" style={{ color: docTheme.subtext }}>{typeLabel}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="font-mono font-semibold text-base" style={{ color: docTheme.text }}>{doc.proposalNumber}</p>
              {doc.expiresAt && (
                <p className="text-xs mt-0.5" style={{ color: docTheme.subtext }}>Expires: {fmtDate(doc.expiresAt)}</p>
              )}
            </div>
          </div>
        </div>

        {/* Card body */}
        <div className="bg-white rounded-b-2xl shadow-lg px-8 py-8">
          {/* Status banner */}
          <StatusBanner
            status={doc.status}
            typeLabel={typeLabel}
            requiresSignatureCapture={requiresSignatureCapture}
          />

          {/* Response result banner */}
          {responseResult === 'accept' && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-800 rounded-xl px-5 py-4 mb-8">
              <CheckCircle size={20} />
              <div>
                <p className="font-semibold">
                  You've {requiresSignatureCapture ? 'signed' : 'accepted'} this {typeLabel.toLowerCase()}!
                </p>
                <p className="text-sm mt-0.5">{issuerProfile.issuerName || BRAND.name} has been notified and will be in touch shortly.</p>
              </div>
            </div>
          )}
          {responseResult === 'decline' && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-800 rounded-xl px-5 py-4 mb-8">
              <XCircle size={20} />
              <div>
                <p className="font-semibold">You've declined this {typeLabel.toLowerCase()}.</p>
                <p className="text-sm mt-0.5">{issuerProfile.issuerName || BRAND.name} has been notified. Thank you for your time.</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm mb-6">{error}</div>
          )}

          {/* Prepared for / prepared by */}
          <div className="grid grid-cols-2 gap-6 mb-8 text-sm">
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Prepared For</p>
              {doc.recipientName && <p className="font-semibold text-gray-900">{doc.recipientName}</p>}
              {doc.recipientCompany && <p className="text-gray-600">{doc.recipientCompany}</p>}
              {doc.recipientEmail && <p className="text-gray-500">{doc.recipientEmail}</p>}
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-1">Prepared By</p>
              <p className="font-semibold text-gray-900">{doc.preparedByName || issuerProfile.issuerName || BRAND.name}</p>
              {doc.preparedByTitle && <p className="text-gray-600">{doc.preparedByTitle}</p>}
              {issuerProfile.legalAddressFormatted ? (
                <p className="text-gray-500 whitespace-pre-line">{issuerProfile.legalAddressFormatted}</p>
              ) : null}
              {issuerProfile.issuerEmail ? <p className="text-gray-500">{issuerProfile.issuerEmail}</p> : null}
            </div>
          </div>

          {showsCommercialScope && (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
              <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Commercial Scope</p>
              <p className="mt-1">{commercialScopeBanner}</p>
            </div>
          )}

          {/* Title */}
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{doc.title}</h2>

          {/* Content sections */}
          {previewSections.map((section) => (
            <div key={section.key} className="border rounded-xl mb-3 overflow-hidden">
              <button
                type="button"
                onClick={() => toggleSection(section.key)}
                className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-800"
              >
                <span>{section.label || section.key}</span>
                {openSections[section.key] ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </button>
              {openSections[section.key] && <div className="px-5 py-4 space-y-4">{renderSectionBlocks(section, doc.type)}</div>}
            </div>
          ))}

          {isMsa && (doc.exhibitActivations || []).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Activated Exhibits</h3>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-3 text-left">Exhibit</th>
                      <th className="px-4 py-3 text-left">Service Line</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Effective Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {doc.exhibitActivations.map((item, idx) => (
                      <tr key={`${item.code || idx}-${idx}`}>
                        <td className="px-4 py-3 text-gray-900">
                          <div className="font-medium">{item.code ? `Exhibit ${item.code}` : 'Exhibit'}</div>
                          <div className="text-xs text-gray-500">{item.name}</div>
                        </td>
                        <td className="px-4 py-3 text-gray-700 capitalize">{item.serviceKey || 'other'}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${item.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                            {item.enabled ? 'Active' : 'Not active'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {item.effectiveDate ? fmtDate(item.effectiveDate) : '—'}
                          {item.notes ? <div className="mt-1 text-xs text-gray-500">{item.notes}</div> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* MSA Exhibits */}
          {isMsa && (doc.exhibits || []).length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Exhibits</h3>
              {doc.exhibits.map((ex, idx) => (
                <div key={idx} className="border rounded-xl mb-3 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleSection(`exhibit-${idx}`)}
                    className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 text-sm font-semibold text-gray-800"
                  >
                    <span>Exhibit {String.fromCharCode(65 + idx)}: {ex.name}</span>
                    {openSections[`exhibit-${idx}`] ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
                  </button>
                  {openSections[`exhibit-${idx}`] && <div className="px-5 py-4 space-y-4">{renderSectionBlocks(ex, doc.type)}</div>}
                </div>
              ))}
            </div>
          )}

          {/* Estimate table (proposals) */}
          {!isMsa && lineItems.length > 0 && (!hasInvestmentSection || doc.type === 'proposal') && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Investment Summary</h3>
              <div className="border rounded-xl overflow-x-auto mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b text-xs text-gray-500">
                      <th className="px-4 py-3 text-left">Service</th>
                      <th className="px-4 py-3 text-center">Qty</th>
                      <th className="px-4 py-3 text-right">Rate</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      {(isSelectableProposal && !isTerminal || doc.acceptanceSelection) && (
                        <th className="px-4 py-3 text-right">Your choice</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lineItems.map((item, idx) => (
                      <tr key={String(item._id || item.id || idx)}>
                        <td className="px-4 py-3 text-gray-800">{item.description}</td>
                        <td className="px-4 py-3 text-center text-gray-600">{item.quantity}</td>
                        <td className="px-4 py-3 text-right text-gray-600">{fmtCurrency(item.unitPrice)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmtCurrency(item.amount)}</td>
                        {(isSelectableProposal && !isTerminal || doc.acceptanceSelection) && (
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            {doc.acceptanceSelection ? (
                              <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                                doc.acceptanceSelection.selectedLineItemIds?.includes(String(item._id || item.id))
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-600'
                              }`}>
                                {doc.acceptanceSelection.selectedLineItemIds?.includes(String(item._id || item.id)) ? 'Accepted' : 'Skipped'}
                              </span>
                            ) : (
                              <div className="inline-flex rounded-lg border p-0.5">
                                {[
                                  ['accept', 'Accept'],
                                  ['skip', 'Skip'],
                                ].map(([choice, label]) => {
                                  const selected = selectedLineItemIds.includes(String(item._id || item.id));
                                  const active = choice === 'accept' ? selected : !selected;
                                  return (
                                    <button
                                      key={choice}
                                      type="button"
                                      onClick={() => setSelectedLineItemIds((current) => {
                                        const id = String(item._id || item.id);
                                        if (choice === 'accept') return current.includes(id) ? current : [...current, id];
                                        return current.filter((value) => value !== id);
                                      })}
                                      className={`rounded-md px-2.5 py-1 text-xs font-medium ${active
                                        ? choice === 'accept' ? 'bg-green-600 text-white' : 'bg-gray-600 text-white'
                                        : 'text-gray-600 hover:bg-gray-100'}`}
                                      aria-pressed={active}
                                    >
                                      {label}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-sm text-right space-y-1">
                <div className="flex justify-end gap-12 text-gray-600">
                  <span>{doc.acceptanceSelection ? 'Accepted subtotal' : isSelectableProposal && !isTerminal ? 'Selected subtotal' : 'Subtotal'}</span><span>{fmtCurrency(totals.subtotal)}</span>
                </div>
                {totals.discountAmount > 0 && (
                  <div className="flex justify-end gap-12 text-red-600">
                    <span>Discount</span><span>-{fmtCurrency(totals.discountAmount)}</span>
                  </div>
                )}
                {totals.taxAmount > 0 && (
                  <div className="flex justify-end gap-12 text-gray-600">
                    <span>Tax</span><span>{fmtCurrency(totals.taxAmount)}</span>
                  </div>
                )}
                <div className="flex justify-end gap-12 font-bold text-gray-900 text-lg border-t pt-2 mt-1">
                  <span>{doc.acceptanceSelection ? 'Accepted total' : isSelectableProposal && !isTerminal ? 'Selected total' : 'Total'}</span><span>{fmtCurrency(totals.total)}</span>
                </div>
              </div>
              {isSelectableProposal && !isTerminal && (
                <p className="mt-3 text-xs text-gray-500 text-right">
                  {selectedLineItemIds.length} of {lineItems.length} services selected. Your confirmed selection will define the accepted scope.
                </p>
              )}
              {doc.additionalNotes && (
                <p className="text-xs text-gray-500 mt-3 italic">{doc.additionalNotes}</p>
              )}
            </div>
          )}

          {lmsCommercial && (
            <div className="mt-8">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">LMS Commercial Scope</h3>
              <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
                <div className="grid grid-cols-1 divide-y divide-gray-200 text-sm md:grid-cols-2 md:divide-x md:divide-y-0">
                  <div className="px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Pricing Model</p>
                    <p className="mt-1 font-medium text-gray-900 capitalize">{String(lmsCommercial.pricingModel || '').replace(/_/g, ' ') || '—'}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Commercial Scope</p>
                    <p className="mt-1 font-medium text-gray-900 capitalize">{String(lmsCommercial.scopeType || '').replace(/_/g, ' ') || '—'}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Included Facilities</p>
                    <p className="mt-1 font-medium text-gray-900">{Number(lmsCommercial.includedFacilityCount || 1)}</p>
                  </div>
                  <div className="px-4 py-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Delivery Mode</p>
                    <p className="mt-1 font-medium text-gray-900 capitalize">{String(lmsCommercial.deliveryMode || '').replace(/_/g, ' ') || '—'}</p>
                  </div>
                  <div className="px-4 py-3 md:col-span-2">
                    <p className="text-xs uppercase tracking-wide text-gray-500">Commercial Package</p>
                    <p className="mt-1 font-medium text-gray-900">
                      {soldPackageTitles.length ? soldPackageTitles.join(', ') : 'No predefined package captured'}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      Sold course set: {soldCourseCount} course{soldCourseCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Accept / Decline actions */}
          {!isTerminal && !responseResult && (
            <div className="mt-10 border-t pt-8">
              <p className="text-sm text-gray-600 mb-5 text-center">
                {requiresSignatureCapture
                  ? `Please review the ${typeLabel.toLowerCase()} above and sign or decline below.`
                  : `Please review the ${typeLabel.toLowerCase()} above and indicate your response below.`}
              </p>

              {showAcceptForm ? (
                <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                  {requiresSignatureCapture ? (
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-green-900 mb-1">Review &amp; Sign</p>
                        <p className="text-xs text-green-700">
                          Enter the signer details below, then complete the electronic signature flow for this {typeLabel.toLowerCase()}.
                        </p>
                      </div>
                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                          <input
                            type="text"
                            value={signerName}
                            onChange={(e) => setSignerName(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            placeholder="Type your full legal name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                          <input
                            type="email"
                            value={signerEmail}
                            onChange={(e) => setSignerEmail(e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 text-sm"
                            placeholder="name@company.com"
                          />
                        </div>
                      </div>
                      {signerName.trim() && signerEmail.trim() ? (
                        <EnhancedSignatureCapture
                          signerInfo={{ name: signerName.trim(), email: signerEmail.trim() }}
                          documentTitle={doc.title || typeLabel}
                          isLoading={responding}
                          onCancel={() => setShowAcceptForm(false)}
                          onSignatureComplete={async ({ signatureData, signerName: signedName, signerEmail: signedEmail, metadata }) => {
                            await respond('accept', {
                              signerName: signedName,
                              signerEmail: signedEmail,
                              signatureData,
                              metadata,
                            });
                          }}
                        />
                      ) : (
                        <div className="rounded-lg border border-dashed border-green-300 bg-white/70 px-4 py-5 text-sm text-gray-600">
                          Enter the signer name and email above to continue to the signature canvas.
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-green-900 mb-1">Review &amp; Accept</p>
                      <p className="text-xs text-green-700 mb-4">
                        By typing your name and clicking "I Accept", you are electronically signing this
                        {' '}{typeLabel.toLowerCase()} on behalf of {doc.recipientCompany || doc.recipientName || 'your organization'}.
                        Your acceptance applies to the services selected above and their recalculated total.
                      </p>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={signerName}
                        onChange={(e) => setSignerName(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
                        placeholder="Type your full legal name"
                      />
                      <label className="flex items-start gap-2 mb-4 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={signerAgreed}
                          onChange={(e) => setSignerAgreed(e.target.checked)}
                          className="mt-0.5"
                        />
                        <span className="text-xs text-gray-600">
                          I have read and agree to the terms for the selected services in this {typeLabel.toLowerCase()}.
                          I understand this electronic acceptance is legally equivalent to a handwritten signature.
                        </span>
                      </label>
                      <div className="flex gap-3 justify-end">
                        <button
                          onClick={() => { setShowAcceptForm(false); setSignerName(doc.recipientName || ''); setSignerAgreed(false); }}
                          className="px-4 py-2 border rounded-lg text-sm"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => respond('accept')}
                          disabled={responding || !signerName.trim() || !signerAgreed || (isSelectableProposal && selectedLineItemIds.length === 0)}
                          className={`inline-flex items-center gap-2 px-5 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-50 ${docTheme.cta}`}
                        >
                          <CheckCircle size={15} />
                          {responding ? 'Processing…' : 'I Accept'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              ) : showDeclineForm ? (
                <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                  <p className="text-sm font-semibold text-red-800 mb-3">
                    Let us know why you're declining (optional):
                  </p>
                  <textarea
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    rows={3}
                    className="w-full border border-red-200 rounded-lg px-3 py-2 text-sm mb-4"
                    placeholder="Budget concerns, not the right time, need different scope…"
                  />
                  <div className="flex gap-3 justify-end">
                    <button
                      onClick={() => setShowDeclineForm(false)}
                      className="px-4 py-2 border rounded-lg text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => respond('decline')}
                      disabled={responding}
                      className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                    >
                      {responding ? 'Sending…' : 'Confirm Decline'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-4 justify-center">
                  <button
                    onClick={() => respond('accept')}
                    disabled={responding || (isSelectableProposal && selectedLineItemIds.length === 0)}
                    className={`inline-flex items-center gap-2 px-8 py-3 text-white rounded-xl font-semibold text-base disabled:opacity-50 shadow-sm ${docTheme.cta}`}
                  >
                    <CheckCircle size={18} />
                    {requiresSignatureCapture ? `Sign ${typeLabel}` : `Accept ${typeLabel}`}
                  </button>
                  <button
                    onClick={() => respond('decline')}
                    disabled={responding}
                    className="inline-flex items-center gap-2 px-8 py-3 border-2 border-red-200 text-red-700 rounded-xl font-semibold text-base hover:bg-red-50 disabled:opacity-50"
                  >
                    <XCircle size={18} />
                    Decline
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t text-center text-xs text-gray-400 space-y-1">
            <p>(c) {new Date().getFullYear()} {issuerProfile.issuerName || BRAND.name} - All rights reserved</p>
            <p>This {typeLabel.toLowerCase()} is confidential and intended only for the recipient above.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
